// src/sections/gamification/plays/artKit.jsx
// The plays art system (owner direction 2026-07-06): modern stylised-cartoon
// nightlife rewards — bold rounded shapes, layered gradients, specular
// highlights and NEON RIM LIGHTING on a dark atmospheric stage. No emoji, no
// flat placeholders. Every reward object is a hand-built inline SVG so it
// stays crisp, tintable and animatable with zero asset pipeline.

import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import { Hero } from './uiKit';

// ---- Club Astra plays palette ----
export const ASTRA = {
  bgDeep: '#0b0716',
  bgMid: '#1a1030',
  violet: '#8b5cf6',
  violetSoft: '#a78bfa',
  cyan: '#22d3ee',
  gold: '#f5c644',
  goldDeep: '#c8961a',
  cream: '#fff6d6',
  ink: '#14101f'
};

// Stage backdrop for every experience: violet stage wash + cyan floor bounce
// over the deep night gradient. Palette discipline (owner reference
// 2026-07-06): black/purple stage, cyan+violet accents only — no pink wash.
export const stageBackground = `
  radial-gradient(700px 380px at 50% -8%, rgba(139,92,246,0.26) 0%, transparent 60%),
  radial-gradient(520px 300px at 18% 108%, rgba(139,92,246,0.14) 0%, transparent 60%),
  radial-gradient(520px 300px at 82% 108%, rgba(34,211,238,0.1) 0%, transparent 60%),
  linear-gradient(180deg, #150e26 0%, #0b0716 70%, #060410 100%)
`;

// Shared SVG defs (gradients + glow filters), included once per SVG via <Defs/>.
function Defs({ id }) {
  return (
    <defs>
      <linearGradient id={`${id}-gold`} x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#ffe9a3" />
        <stop offset="0.5" stopColor="#f5c644" />
        <stop offset="1" stopColor="#b97f12" />
      </linearGradient>
      <linearGradient id={`${id}-violet`} x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#b79bff" />
        <stop offset="0.55" stopColor="#8b5cf6" />
        <stop offset="1" stopColor="#5b34c4" />
      </linearGradient>
      {/* velvet crimson — reserved for the rope only (a velvet rope IS red) */}
      <linearGradient id={`${id}-velvet`} x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#d94f72" />
        <stop offset="0.55" stopColor="#a82547" />
        <stop offset="1" stopColor="#6e1230" />
      </linearGradient>
      <linearGradient id={`${id}-cyan`} x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#a5f3ff" />
        <stop offset="0.55" stopColor="#22d3ee" />
        <stop offset="1" stopColor="#0e7c95" />
      </linearGradient>
      {/* dark champagne glass — near-black bottle green, cyan rim carries it */}
      <linearGradient id={`${id}-glass`} x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#33544a" />
        <stop offset="0.5" stopColor="#132a22" />
        <stop offset="1" stopColor="#07120d" />
      </linearGradient>
      <filter id={`${id}-glow`} x="-40%" y="-40%" width="180%" height="180%">
        <feGaussianBlur stdDeviation="6" result="b" />
        <feMerge>
          <feMergeNode in="b" />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>
    </defs>
  );
}
Defs.propTypes = { id: PropTypes.string.isRequired };

// Soft contact shadow used under every object.
function GroundShadow({ cx = 60, cy = 112, rx = 34 }) {
  return <ellipse cx={cx} cy={cy} rx={rx} ry={7} fill="rgba(0,0,0,0.45)" />;
}
GroundShadow.propTypes = { cx: PropTypes.number, cy: PropTypes.number, rx: PropTypes.number };

// ---- Reward objects (120x120 viewBox each) ----

export function BonusCoin({ size = 120 }) {
  const id = 'coin';
  return (
    <svg width={size} height={size} viewBox="0 0 120 120">
      <Defs id={id} />
      <GroundShadow />
      {/* coin edge (thickness) */}
      <ellipse cx="60" cy="64" rx="42" ry="42" fill="#8a5f0e" />
      <ellipse cx="60" cy="58" rx="42" ry="42" fill={`url(#${id}-gold)`} />
      {/* inner face */}
      <ellipse cx="60" cy="58" rx="32" ry="32" fill="#e3b53a" />
      <ellipse cx="60" cy="58" rx="32" ry="32" fill="none" stroke="#9a6d10" strokeWidth="2" opacity="0.5" />
      {/* A monogram */}
      <text x="60" y="70" textAnchor="middle" fontFamily="system-ui" fontWeight="900" fontSize="36" fill="#7a530a">
        A
      </text>
      {/* specular sweep */}
      <path d="M28 40 Q54 18 92 34 Q66 26 34 48 Z" fill="rgba(255,255,255,0.55)" />
      {/* neon rim */}
      <ellipse
        cx="60"
        cy="58"
        rx="42"
        ry="42"
        fill="none"
        stroke={ASTRA.gold}
        strokeWidth="2.5"
        opacity="0.9"
        filter={`url(#${id}-glow)`}
      />
    </svg>
  );
}

export function XPToken({ size = 120 }) {
  const id = 'xp';
  return (
    <svg width={size} height={size} viewBox="0 0 120 120">
      <Defs id={id} />
      <GroundShadow />
      {/* rounded hex token */}
      <path
        d="M60 12 L98 34 L98 82 L60 104 L22 82 L22 34 Z"
        fill={`url(#${id}-violet)`}
        stroke="#3d2775"
        strokeWidth="3"
        strokeLinejoin="round"
      />
      <path d="M60 22 L89 39 L89 77 L60 94 L31 77 L31 39 Z" fill="#4a2d94" opacity="0.55" />
      {/* lightning bolt */}
      <path
        d="M66 30 L42 64 L58 64 L52 90 L80 52 L62 52 Z"
        fill={`url(#${id}-cyan)`}
        stroke="#0b6c82"
        strokeWidth="2"
        strokeLinejoin="round"
        filter={`url(#${id}-glow)`}
      />
      {/* specular */}
      <path d="M34 32 Q60 16 86 30 Q62 24 38 42 Z" fill="rgba(255,255,255,0.4)" />
      {/* neon rim */}
      <path
        d="M60 12 L98 34 L98 82 L60 104 L22 82 L22 34 Z"
        fill="none"
        stroke={ASTRA.cyan}
        strokeWidth="2"
        opacity="0.85"
        filter={`url(#${id}-glow)`}
      />
    </svg>
  );
}

export function DrinkCard({ size = 120 }) {
  const id = 'drink';
  return (
    <svg width={size} height={size} viewBox="0 0 120 120">
      <Defs id={id} />
      <GroundShadow />
      {/* card */}
      <rect x="20" y="26" width="80" height="78" rx="14" fill={`url(#${id}-violet)`} stroke="rgba(20,10,45,0.6)" strokeWidth="2" />
      <rect x="26" y="32" width="68" height="66" rx="10" fill="#4a2d94" opacity="0.55" />
      {/* cocktail glass */}
      <path d="M42 46 L78 46 L60 68 Z" fill={`url(#${id}-cyan)`} stroke="#0b6c82" strokeWidth="2.5" strokeLinejoin="round" />
      <line x1="60" y1="68" x2="60" y2="86" stroke="#a5f3ff" strokeWidth="3.5" strokeLinecap="round" />
      <line x1="50" y1="88" x2="70" y2="88" stroke="#a5f3ff" strokeWidth="3.5" strokeLinecap="round" />
      {/* garnish + umbrella */}
      <circle cx="53" cy="50" r="4" fill={ASTRA.gold} stroke="#9a6d10" strokeWidth="1.5" />
      <path d="M64 38 L76 30 L74 44 Z" fill={ASTRA.gold} stroke="#9a6d10" strokeWidth="1.5" strokeLinejoin="round" />
      {/* specular */}
      <path d="M26 34 Q60 24 94 34 Q64 30 30 44 Z" fill="rgba(255,255,255,0.35)" />
      {/* neon rim */}
      <rect
        x="20"
        y="26"
        width="80"
        height="78"
        rx="14"
        fill="none"
        stroke={ASTRA.cyan}
        strokeWidth="1.5"
        opacity="0.75"
        filter={`url(#${id}-glow)`}
      />
    </svg>
  );
}

export function VIPPass({ size = 120 }) {
  const id = 'vip';
  return (
    <svg width={size} height={size} viewBox="0 0 120 120">
      <Defs id={id} />
      <GroundShadow />
      {/* lanyard */}
      <path d="M50 8 Q60 26 70 8" fill="none" stroke="#3d2775" strokeWidth="5" strokeLinecap="round" />
      <rect x="53" y="18" width="14" height="10" rx="3" fill="#3d2775" />
      {/* pass body */}
      <rect x="24" y="26" width="72" height="82" rx="14" fill={`url(#${id}-violet)`} stroke="#31205e" strokeWidth="3" />
      <rect x="30" y="32" width="60" height="70" rx="10" fill="#4a2d94" opacity="0.5" />
      {/* star */}
      <path
        d="M60 44 L66 57 L80 58 L70 68 L73 82 L60 74 L47 82 L50 68 L40 58 L54 57 Z"
        fill={`url(#${id}-gold)`}
        stroke="#9a6d10"
        strokeWidth="2"
        strokeLinejoin="round"
        filter={`url(#${id}-glow)`}
      />
      <text x="60" y="99" textAnchor="middle" fontFamily="system-ui" fontWeight="900" fontSize="15" letterSpacing="3" fill={ASTRA.cream}>
        VIP
      </text>
      <path d="M30 34 Q60 24 90 34 Q62 30 34 44 Z" fill="rgba(255,255,255,0.35)" />
      <rect
        x="24"
        y="26"
        width="72"
        height="82"
        rx="14"
        fill="none"
        stroke={ASTRA.violetSoft}
        strokeWidth="2"
        opacity="0.9"
        filter={`url(#${id}-glow)`}
      />
    </svg>
  );
}

export function GoldWristband({ size = 120 }) {
  const id = 'band';
  return (
    <svg width={size} height={size} viewBox="0 0 120 120">
      <Defs id={id} />
      <GroundShadow />
      {/* band torus */}
      <ellipse cx="60" cy="62" rx="40" ry="30" fill="none" stroke={`url(#${id}-gold)`} strokeWidth="18" />
      <ellipse cx="60" cy="62" rx="40" ry="30" fill="none" stroke="#8a5f0e" strokeWidth="18" opacity="0.28" transform="translate(0 4)" />
      {/* clasp plate */}
      <rect x="42" y="24" width="36" height="18" rx="8" fill={`url(#${id}-gold)`} stroke="#9a6d10" strokeWidth="2" />
      <text x="60" y="37" textAnchor="middle" fontFamily="system-ui" fontWeight="900" fontSize="10" letterSpacing="1" fill="#7a530a">
        ASTRA
      </text>
      {/* specular */}
      <path d="M28 48 Q42 36 58 36 Q40 44 34 58 Z" fill="rgba(255,255,255,0.5)" />
      <ellipse cx="60" cy="62" rx="49" ry="39" fill="none" stroke={ASTRA.gold} strokeWidth="2" opacity="0.75" filter={`url(#${id}-glow)`} />
    </svg>
  );
}

export function GuestlistStamp({ size = 120 }) {
  const id = 'stamp';
  return (
    <svg width={size} height={size} viewBox="0 0 120 120">
      <Defs id={id} />
      <GroundShadow />
      {/* handle */}
      <rect x="50" y="14" width="20" height="26" rx="9" fill={`url(#${id}-violet)`} stroke="rgba(20,10,45,0.6)" strokeWidth="2" />
      <rect x="40" y="38" width="40" height="14" rx="6" fill="#4a2d94" stroke="rgba(20,10,45,0.6)" strokeWidth="2" />
      {/* stamp base */}
      <rect x="30" y="50" width="60" height="16" rx="7" fill={`url(#${id}-violet)`} stroke="#31205e" strokeWidth="2.5" />
      {/* ink impression */}
      <circle cx="60" cy="88" r="20" fill="none" stroke={ASTRA.cyan} strokeWidth="3.5" strokeDasharray="5 4" filter={`url(#${id}-glow)`} />
      <path d="M60 78 L63 85 L71 85 L65 90 L67 98 L60 93 L53 98 L55 90 L49 85 L57 85 Z" fill={ASTRA.cyan} opacity="0.95" />
      <path d="M52 18 Q60 12 68 18 Q60 16 52 24 Z" fill="rgba(255,255,255,0.45)" />
    </svg>
  );
}

export function ChampagneBottleIcon({ size = 120, cork = true }) {
  const id = 'champ';
  return (
    <svg width={size} height={size} viewBox="0 0 120 120">
      <Defs id={id} />
      <GroundShadow cx={60} cy={112} rx={26} />
      {/* cork (hidden when the host component animates its own) */}
      {cork ? <rect x="52" y="6" width="16" height="14" rx="5" fill="#d7a86e" stroke="#8a5f34" strokeWidth="2" /> : null}
      {/* gold foil neck */}
      <path d="M50 18 L70 18 L68 42 L52 42 Z" fill={`url(#${id}-gold)`} stroke="#9a6d10" strokeWidth="2" />
      {/* body — dark champagne glass, cyan rim light carries the silhouette */}
      <path
        d="M52 42 Q30 58 30 78 Q30 108 60 108 Q90 108 90 78 Q90 58 68 42 Z"
        fill={`url(#${id}-glass)`}
        stroke="rgba(4,14,9,0.8)"
        strokeWidth="2"
      />
      {/* label */}
      <rect x="42" y="68" width="36" height="24" rx="6" fill={ASTRA.cream} stroke="#c9b98a" strokeWidth="2" />
      <text
        x="60"
        y="79"
        textAnchor="middle"
        textLength="28"
        lengthAdjust="spacingAndGlyphs"
        fontFamily="system-ui"
        fontWeight="900"
        fontSize="7"
        fill="#3d2775"
      >
        CLUB ASTRA
      </text>
      <text
        x="60"
        y="88"
        textAnchor="middle"
        textLength="22"
        lengthAdjust="spacingAndGlyphs"
        fontFamily="system-ui"
        fontWeight="600"
        fontSize="5.5"
        fill="#8a7f66"
      >
        RÉSERVE
      </text>
      {/* specular streak */}
      <path d="M40 56 Q36 78 42 96 Q34 80 38 58 Z" fill="rgba(255,255,255,0.5)" />
      {/* neon rim */}
      <path
        d="M52 42 Q30 58 30 78 Q30 108 60 108 Q90 108 90 78 Q90 58 68 42 Z"
        fill="none"
        stroke={ASTRA.cyan}
        strokeWidth="2"
        opacity="0.55"
        filter={`url(#${id}-glow)`}
      />
    </svg>
  );
}

export function VelvetRope({ size = 120 }) {
  const id = 'rope';
  return (
    <svg width={size} height={size} viewBox="0 0 120 120">
      <Defs id={id} />
      <ellipse cx="30" cy="110" rx="16" ry="5" fill="rgba(0,0,0,0.45)" />
      <ellipse cx="90" cy="110" rx="16" ry="5" fill="rgba(0,0,0,0.45)" />
      {/* stanchions */}
      {[30, 90].map((x) => (
        <g key={x}>
          <rect x={x - 4} y="34" width="8" height="72" rx="3" fill={`url(#${id}-gold)`} stroke="#9a6d10" strokeWidth="1.5" />
          <ellipse cx={x} cy="106" rx="14" ry="6" fill={`url(#${id}-gold)`} stroke="#9a6d10" strokeWidth="1.5" />
          <circle cx={x} cy="30" r="7" fill={`url(#${id}-gold)`} stroke="#9a6d10" strokeWidth="1.5" />
        </g>
      ))}
      {/* rope — velvet crimson (the one earned exception to the palette) */}
      <path d="M30 40 Q60 68 90 40" fill="none" stroke={`url(#${id}-velvet)`} strokeWidth="11" strokeLinecap="round" />
      <path
        d="M30 40 Q60 68 90 40"
        fill="none"
        stroke="rgba(255,255,255,0.3)"
        strokeWidth="3"
        strokeLinecap="round"
        transform="translate(0 -2.5)"
      />
      <path d="M30 40 Q60 68 90 40" fill="none" stroke="#a82547" strokeWidth="12" opacity="0.3" filter={`url(#${id}-glow)`} />
    </svg>
  );
}

export function GiftCrate({ size = 120, lidOffset = 0, seamGlow = 0 }) {
  const id = 'crate';
  return (
    <svg width={size} height={size} viewBox="0 0 120 120">
      <Defs id={id} />
      <GroundShadow />
      {/* body */}
      <rect x="24" y="46" width="72" height="58" rx="10" fill={`url(#${id}-violet)`} stroke="#31205e" strokeWidth="3" />
      <rect x="30" y="52" width="60" height="46" rx="7" fill="#4a2d94" opacity="0.5" />
      {/* vertical ribbon */}
      <rect x="52" y="46" width="16" height="58" rx="4" fill={`url(#${id}-gold)`} stroke="#9a6d10" strokeWidth="2" />
      {/* seam glow (builds with taps) */}
      <rect x="26" y="43" width="68" height="5" rx="2.5" fill={ASTRA.gold} opacity={seamGlow} filter={`url(#${id}-glow)`} />
      {/* lid (animates up via lidOffset) */}
      <g transform={`translate(0 ${-lidOffset}) rotate(${-lidOffset * 0.35} 60 38)`}>
        <rect x="18" y="28" width="84" height="18" rx="8" fill={`url(#${id}-violet)`} stroke="#31205e" strokeWidth="3" />
        <rect x="52" y="28" width="16" height="18" fill={`url(#${id}-gold)`} stroke="#9a6d10" strokeWidth="2" />
        {/* bow */}
        <path d="M60 28 Q46 10 38 22 Q34 30 52 30 Z" fill={`url(#${id}-gold)`} stroke="#9a6d10" strokeWidth="2" strokeLinejoin="round" />
        <path d="M60 28 Q74 10 82 22 Q86 30 68 30 Z" fill={`url(#${id}-gold)`} stroke="#9a6d10" strokeWidth="2" strokeLinejoin="round" />
        <circle cx="60" cy="27" r="6" fill="#ffe9a3" stroke="#9a6d10" strokeWidth="2" />
        <path d="M22 32 Q60 22 98 32 Q64 28 26 40 Z" fill="rgba(255,255,255,0.35)" />
      </g>
      {/* neon rim */}
      <rect
        x="24"
        y="46"
        width="72"
        height="58"
        rx="10"
        fill="none"
        stroke={ASTRA.violetSoft}
        strokeWidth="2"
        opacity="0.85"
        filter={`url(#${id}-glow)`}
      />
    </svg>
  );
}
GiftCrate.propTypes = { size: PropTypes.number, lidOffset: PropTypes.number, seamGlow: PropTypes.number };

// ---- outcome → object + tier styling ----

// `asset` is the drop-in slot name: put src/assets/plays/<asset>.png (a
// rendered, transparent-background object matching the bottle reference) in
// the repo and the SVG fallback is replaced everywhere automatically.
export function rewardArt(outcome) {
  if (!outcome || outcome.type === 'nothing') {
    return { Obj: VelvetRope, asset: 'velvet-rope', rim: ASTRA.violetSoft, glow: 'rgba(139,92,246,0.4)', headlineColor: '#d9cfff' };
  }
  if (outcome.type === 'cash') {
    return { Obj: BonusCoin, asset: 'astra-coin', rim: ASTRA.gold, glow: 'rgba(245,198,68,0.5)', headlineColor: ASTRA.cream };
  }
  if (outcome.type === 'perk') {
    const t = outcome.perk?.type;
    if (t === 'vipBooth')
      return { Obj: VIPPass, asset: 'vip-pass', rim: ASTRA.violetSoft, glow: 'rgba(139,92,246,0.55)', headlineColor: '#e6dcff' };
    if (t === 'freeEntry')
      return { Obj: GuestlistStamp, asset: 'guestlist-stamp', rim: ASTRA.cyan, glow: 'rgba(34,211,238,0.5)', headlineColor: '#d3f6fd' };
    if (t === 'crossEventEntry')
      return { Obj: GoldWristband, asset: 'gold-wristband', rim: ASTRA.gold, glow: 'rgba(245,198,68,0.5)', headlineColor: ASTRA.cream };
    return { Obj: DrinkCard, asset: 'drink-card', rim: ASTRA.cyan, glow: 'rgba(34,211,238,0.45)', headlineColor: '#d3f6fd' };
  }
  return { Obj: XPToken, asset: 'xp-token', rim: ASTRA.cyan, glow: 'rgba(34,211,238,0.55)', headlineColor: '#d3f6fd' };
}

// Level name → trophy render slot (owner assets): matched by tier keyword so
// any ladder whose levels are called Bronze/Silver/Gold/Platinum/Legend gets
// its trophy; unmatched names return null (callers keep their icon fallback).
const TROPHY_TIERS = ['legend', 'platinum', 'gold', 'silver', 'bronze'];
export function trophySlot(levelName) {
  const n = String(levelName || '').toLowerCase();
  const tier = TROPHY_TIERS.find((t) => n.includes(t));
  return tier ? `trophy-${tier}` : null;
}

// Perk type → art, for perk surfaces outside the games (shop, My Perks,
// vouchers). Same slots/fallbacks as rewardArt's perk branch.
export function perkArt(perkType) {
  return rewardArt({ type: 'perk', perk: { type: perkType } });
}

// Reward object with an idle float, drop-in for reveals. Renders the
// rendered-image asset when present (uiKit Hero slot), else the SVG fallback.
export function FloatingReward({ outcome, size = 140 }) {
  const { Obj, asset, glow } = rewardArt(outcome);
  return (
    <Box
      sx={{
        display: 'inline-flex',
        animation: 'rewardFloat 2.6s ease-in-out infinite',
        '@keyframes rewardFloat': {
          '0%, 100%': { transform: 'translateY(0) rotate(-2deg)' },
          '50%': { transform: 'translateY(-9px) rotate(2deg)' }
        }
      }}
    >
      <Hero asset={asset} size={size} glow={glow} fallback={<Obj size={size} />} />
    </Box>
  );
}
FloatingReward.propTypes = { outcome: PropTypes.object, size: PropTypes.number };
